/**
 * The document-validation job, owned globally.
 *
 * The run used to live inside whichever page started it (the create wizard, or
 * the upload-version dialog), so minimising the console or navigating to
 * another route killed it. It lives here instead: the request, the result and
 * the orphan-draft bookkeeping all sit in module scope, `ProcessingJobHost`
 * renders the console from `MainLayout` for the whole life of the job, and
 * `ProcessingStatusBubble` shows the current state on every page while the
 * console is minimised.
 *
 * The page that started the job only reacts to the outcome - `completionTick`
 * tells it to refetch, `reopenPickerTick` tells it to bring the file picker
 * back with `lastError` showing.
 */
import { create } from "zustand";
import {
  uploadGovernanceDocumentFile,
  fetchDocumentSemanticValidation,
  getGovernanceDocumentExtractionStatus,
  buildValidateResultFromSemantic,
  deleteGovernanceDocumentOrphan,
  ApiEnvelopeError,
  type ValidateFileResult,
} from "../api/frameworksApi";
import { showToast } from "../app/components/ui/custom-toast";

export type JobPhase = "running" | "complete" | "error" | "uploading";

/** Which flow started the run: the create wizard, or upload-a-new-version. */
export type JobMode = "create" | "version";

/** How a run ended, once there is nothing left on screen. */
export type JobOutcome = "uploaded" | "discarded" | "closed";

export interface JobError {
  title: string;
  message: string;
  code?: string;
}

export interface ProcessingJob {
  mode: JobMode;
  /** Document the file is validated against. */
  docId: string;
  file: File;
  documentTitle?: string;
  documentType?: string;
  fileName?: string;
  fileSize?: string;
  phase: JobPhase;
  /** Findings from /semantic-validation, once the request has returned. */
  result: ValidateFileResult | null;
  error: JobError | null;
  /** Measured request time; paces the console's closing stages. */
  apiDurationMs: number | null;
  /** True once the console has played its closing stages. */
  consoleFinished: boolean;
  /** Pipeline stage the console is lit on, mirrored for the bubble. */
  stage: number;
}

export interface StartJobParams {
  mode: JobMode;
  docId: string;
  file: File;
  documentTitle?: string;
  documentType?: string;
  fileName?: string;
  fileSize?: string;
  /**
   * Create flow only: the DRAFT that must be deleted if the run is abandoned.
   * Omitted for upload-a-new-version, where the document already exists.
   */
  orphanDocId?: string | null;
}

interface ProcessingJobStore {
  job: ProcessingJob | null;
  /** Console hidden to the status bubble while the run continues. */
  minimized: boolean;
  /**
   * DRAFT created but not yet successfully uploaded. Deleted on cancel,
   * dismiss and tab close so we don't leak rows.
   */
  orphanDocId: string | null;
  /** Bumped on every terminal outcome; pages refetch when it changes. */
  completionTick: number;
  /**
   * What the run ended as, read when `completionTick` changes. "discarded"
   * means the backend soft-deleted the document, so it is gone from the list;
   * "closed" means the user walked away from a clean run without uploading.
   */
  lastOutcome: { mode: JobMode; kind: JobOutcome } | null;
  /** Bumped when the starting flow should bring its file picker back. */
  reopenPickerTick: number;
  /**
   * Read when `reopenPickerTick` changes: which picker, for which document,
   * and why. `docId` lets the flow reselect the document if the user has since
   * navigated somewhere else.
   */
  reopenTarget: { mode: JobMode; docId: string; error: JobError | null } | null;

  /**
   * Track a DRAFT that must not outlive the flow that created it. Called as
   * soon as the create wizard has an id, which is before validation starts.
   */
  setOrphanDoc: (docId: string | null) => void;
  /** Delete the tracked draft now (explicit dismissal of the create wizard). */
  flushOrphanDoc: () => void;
  /** Publish the job and run /semantic-validation behind the console. */
  start: (params: StartJobParams) => Promise<void>;
  /** Upload the validated file. */
  save: () => Promise<void>;
  /** Abandon an in-flight run and return the caller to its file picker. */
  cancel: () => void;
  /** Close a finished run without uploading. */
  dismiss: () => void;
  /** Back to the picker, keeping the draft so it can be re-validated. */
  pickDifferent: () => void;
  /** The console played its closing stages; the result panel can take over. */
  markConsoleFinished: () => void;
  /** Mirror the console's current stage for the bubble. */
  setStage: (stage: number) => void;
  minimize: () => void;
  restore: () => void;
  /** Clear the job outright - nothing else to show. */
  clear: () => void;
}

/* ── Module scope: survives every unmount ───────────────────────────────── */

let abortCtrl: AbortController | null = null;

/* ── Error mapping ──────────────────────────────────────────────────────── */

const ERROR_CODE_MESSAGES: Record<string, (detail: string) => string> = {
  "REG-2001-E": (d) =>
    `We couldn't read this PDF: ${d} Please pick a different file.`,
  "CR-0301-E": () =>
    "This document doesn't have an organization assigned yet. Set the organization on the document first, then try again.",
  "LLM-0005-E": (d) =>
    `${d} Configure an active LLM under LLM Configurations for this organization to enable validation.`,
  "LLM-0001-E": () =>
    "The active LLM rejected the request — its credentials may be invalid. Ask your administrator to check.",
  "LLM-0003-E": () =>
    "The active LLM model is not available. Ask your administrator to check the configuration.",
  "LLM-0004-E": () => "Couldn't reach the LLM provider. Try again in a moment.",
  "LLM-0099-E": () =>
    "Validation result couldn't be parsed. Try again, or pick a different model.",
  "SYS-0002-E": () => "Invalid file. Please pick a PDF.",
  "CR-0102-E": () => "This document is no longer editable.",
  "CR-0212-E": (d) => `File upload failed: ${d}`,
};

function mapError(e: unknown): JobError {
  if (e instanceof ApiEnvelopeError) {
    const mapper = ERROR_CODE_MESSAGES[e.code];
    return {
      title:
        e.code === "LLM-0005-E"
          ? "No active LLM for this document's organization"
          : e.title || "Validation failed",
      message: mapper ? mapper(e.detail) : e.detail,
      code: e.code,
    };
  }
  if (e instanceof TypeError && e.message === "Failed to fetch") {
    return {
      title: "No Response From Server",
      message:
        "The request was sent but the server never responded — this can happen when validation takes too long on a large document. Try again, try a smaller file, or check the server logs for this request if it keeps happening.",
    };
  }
  if (e instanceof Error) {
    return { title: e.name || "Error", message: e.message };
  }
  return { title: "Error", message: "Something went wrong." };
}

function isAbortError(e: unknown): boolean {
  return (
    (e instanceof DOMException && e.name === "AbortError") ||
    (e instanceof Error && e.name === "AbortError")
  );
}

/* ── Extraction-status polling ──────────────────────────────────────────── */

/** How long to wait between successive polls of /extraction-status. */
const EXTRACTION_POLL_INTERVAL_MS = 10_000;
/** Safety cap - stop polling and surface a timeout after this long. */
const EXTRACTION_POLL_TIMEOUT_MS = 10 * 60 * 1000;

/** Promise-based sleep that resolves early if the abort signal fires. */
function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(new DOMException("Aborted", "AbortError"));
      return;
    }
    const t = setTimeout(() => {
      signal.removeEventListener("abort", onAbort);
      resolve();
    }, ms);
    const onAbort = () => {
      clearTimeout(t);
      reject(new DOMException("Aborted", "AbortError"));
    };
    signal.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Polls GET /extraction-status every 10s until the backend reports the
 * uploaded file as COMPLETED. Resolves on success, throws on FAILED status
 * (with the backend's error message when present), aborts cleanly on
 * `signal`, and rejects with a timeout error after 10 minutes so a stuck
 * job doesn't wedge the console forever.
 *
 * A single poll failing (network hiccup / transient 5xx) is treated as
 * "keep polling" - the loop just waits and re-tries, only bailing on
 * abort, explicit FAILED status, or the timeout cap.
 */
async function waitForExtractionComplete(
  docId: string,
  signal: AbortSignal,
): Promise<void> {
  const deadline = Date.now() + EXTRACTION_POLL_TIMEOUT_MS;

  while (true) {
    if (signal.aborted) {
      throw new DOMException("Aborted", "AbortError");
    }
    if (Date.now() > deadline) {
      throw new Error(
        "Document extraction did not complete within 10 minutes. Try again or contact support.",
      );
    }

    let status = "";
    let backendError = "";
    try {
      const res = await getGovernanceDocumentExtractionStatus(docId, signal);
      status = String(res.data?.status ?? "").toUpperCase();
      backendError = String(res.data?.error ?? "");
    } catch (pollErr: unknown) {
      // Abort propagates up; anything else is treated as a transient
      // failure - wait and re-poll so a brief network blip doesn't
      // fail the whole run.
      if (signal.aborted || isAbortError(pollErr)) throw pollErr;
      // Fall through to the sleep + retry below.
    }

    if (
      status === "COMPLETED" ||
      status === "COMPLETE" ||
      status === "SUCCESS"
    ) {
      return;
    }
    if (status === "FAILED" || status === "ERROR") {
      throw new Error(
        backendError || "Document extraction failed on the server.",
      );
    }

    // PENDING / IN_PROGRESS / unknown / transient failure → wait, retry.
    await abortableSleep(EXTRACTION_POLL_INTERVAL_MS, signal);
  }
}

/* ── Store ──────────────────────────────────────────────────────────────── */

export const useProcessingJobStore = create<ProcessingJobStore>((set, get) => {
  /** Best-effort DELETE of the draft this run created, if any. */
  const flushOrphan = () => {
    const id = get().orphanDocId;
    if (!id) return;
    set({ orphanDocId: null });
    deleteGovernanceDocumentOrphan(id);
  };

  /** Merge into the live job, ignoring late updates from a cleared run. */
  const patch = (patch: Partial<ProcessingJob>) => {
    const job = get().job;
    if (!job) return;
    set({ job: { ...job, ...patch } });
  };

  const reset = () => set({ job: null, minimized: false, orphanDocId: null });

  /** Tell the flow that started the run to bring its file picker back. */
  const reopenPicker = (job: ProcessingJob, error: JobError | null) =>
    set({
      reopenTarget: { mode: job.mode, docId: job.docId, error },
      reopenPickerTick: get().reopenPickerTick + 1,
    });

  /** Tell the flow that started the run to refresh what it shows. */
  const signalCompletion = (mode: JobMode, kind: JobOutcome) =>
    set({
      lastOutcome: { mode, kind },
      completionTick: get().completionTick + 1,
    });

  return {
    job: null,
    minimized: false,
    orphanDocId: null,
    completionTick: 0,
    lastOutcome: null,
    reopenPickerTick: 0,
    reopenTarget: null,

    setOrphanDoc: (docId) => set({ orphanDocId: docId }),
    flushOrphanDoc: flushOrphan,

    start: async (params) => {
      abortCtrl?.abort();
      const ctrl = new AbortController();
      abortCtrl = ctrl;

      set({
        minimized: false,
        reopenTarget: null,
        // Keep whatever draft the caller registered before it got here.
        orphanDocId: params.orphanDocId ?? get().orphanDocId,
        job: {
          mode: params.mode,
          docId: params.docId,
          file: params.file,
          documentTitle: params.documentTitle,
          documentType: params.documentType,
          fileName: params.fileName ?? params.file.name,
          fileSize: params.fileSize,
          // Start in "uploading" - the pipeline animation parks at
          // Structure until the store transitions this to "running"
          // (below) once /upload succeeds.
          phase: "uploading",
          result: null,
          error: null,
          apiDurationMs: null,
          consoleFinished: false,
          stage: 0,
        },
      });

      const startedAt = Date.now();
      try {
        /* ── Step 1: /upload ──
           The backend needs the file on disk before /semantic-validation
           can inspect it, and the user asked for the upload to happen the
           moment they click Save in the picker modal (before validation
           kicks off). Passing `ctrl.signal` means a user-initiated
           cancel actually cancels the in-flight upload instead of
           letting it race the orphan-cleanup DELETE.

           Upload errors are handled *inside* this inner try - they
           don't belong in the validation console, since validation
           never got to run. Instead we reset the job and reopen the
           picker with the error surfaced there, so the user sees the
           reason inline (e.g. "Duplicate file") and can pick another
           file without watching a pipeline animation for a run that
           never happened. */
        try {
          await uploadGovernanceDocumentFile(
            params.docId,
            params.file,
            ctrl.signal,
          );
        } catch (uploadErr: unknown) {
          if (ctrl.signal.aborted || isAbortError(uploadErr)) return;

          /* CR-0303-E: the backend's own upload rules rejected the file.
             On a first upload it soft-deletes the draft; on a subsequent
             version it keeps the previous file. Either way the draft is
             either gone or superseded, so we clear the orphan tracker,
             toast the user, and end the flow. */
          if (
            uploadErr instanceof ApiEnvelopeError &&
            uploadErr.code === "CR-0303-E"
          ) {
            const discarded = uploadErr.detail.includes("discarded");
            set({ orphanDocId: null });
            showToast(
              "error",
              discarded
                ? "Draft discarded. The file did not match this document's metadata or org rules. Re-create the document and upload a matching file."
                : "Upload rejected. The new file did not pass validation; the previously-uploaded file is retained.",
            );
            reset();
            signalCompletion(
              params.mode,
              discarded ? "discarded" : "uploaded",
            );
            return;
          }

          /* Any other upload failure (duplicate file, size limit,
             unsupported format, 5xx) → bounce back to the picker.
             Preserve `orphanDocId` (upload didn't complete, so the draft
             is still an orphan that needs cleanup on cancel). */
          const currentJob = get().job;
          const error = mapError(uploadErr);
          set({ job: null, minimized: false });
          if (currentJob) reopenPicker(currentJob, error);
          return;
        }
        if (ctrl.signal.aborted) return;
        // Upload succeeded - file is committed, so drop the orphan-draft
        // tracker: a subsequent tab-close must not fire a stray DELETE.
        // Also transition the phase to "running" so the pipeline
        // animation releases its Structure hold and starts flowing
        // through Chunk / Intelligence / Semantic while the extraction
        // poll + /semantic-validation do their work.
        set({ orphanDocId: null });
        patch({ phase: "running" });

        /* ── Step 2: Poll /extraction-status ──
           The backend needs to finish parsing / chunking the uploaded
           file before /semantic-validation can be called. Poll every
           10s until `data.status` is COMPLETED. Backend-reported
           FAILED status surfaces the error message in the console;
           timeout (10 min cap) surfaces a clear message so a stuck
           job never wedges the run silently. */
        try {
          await waitForExtractionComplete(params.docId, ctrl.signal);
        } catch (extractErr: unknown) {
          if (ctrl.signal.aborted || isAbortError(extractErr)) return;
          patch({ phase: "error", error: mapError(extractErr) });
          return;
        }
        if (ctrl.signal.aborted) return;

        /* ── Step 3: GET /semantic-validation ──
           This is now the sole source of validation data. It drives the
           Validation Feed (passedRules / failedRules / warningRules /
           notApplicableRules), the summary tiles (rules / passed /
           failed / warnings / compliance) and the Seven-Layer panel
           (confidence.byEvidence when the backend sends it). The prior
           /validate-file pre-flight has been removed - failures here
           are terminal for the job, not warnings. */
        const semantic = await fetchDocumentSemanticValidation(
          params.docId,
          ctrl.signal,
        );
        if (ctrl.signal.aborted) return;
        // eslint-disable-next-line no-console
        console.log(
          "[processingJobStore] /semantic-validation response:",
          semantic,
        );

        const result = buildValidateResultFromSemantic(semantic, {
          filename: params.fileName ?? params.file.name,
          docType: params.documentType,
          durationMs: Date.now() - startedAt,
        });
        patch({
          result,
          apiDurationMs: Date.now() - startedAt,
          phase: "complete",
        });
      } catch (err: unknown) {
        if (ctrl.signal.aborted || isAbortError(err)) return;
        // Only /semantic-validation errors reach here - upload errors are
        // handled above and never fall through. The console stays up
        // showing the failure; Close returns the caller to its picker.
        patch({ phase: "error", error: mapError(err) });
      } finally {
        if (abortCtrl === ctrl) abortCtrl = null;
      }
    },

    save: async () => {
      const job = get().job;
      if (!job) return;
      /* The file was already uploaded at the start of the run (see `start`
         above), so this is now a confirm-and-close action rather than a
         second upload. Keep the toast + completion signal so the caller
         page still refetches the list. */
      showToast(
        "success",
        job.mode === "create"
          ? "Document created and file uploaded successfully"
          : "File uploaded successfully.",
      );
      reset();
      signalCompletion(job.mode, "uploaded");
    },

    cancel: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      flushOrphan();
      const job = get().job;
      reset();
      // Bring the starting flow's picker back so the user can retry.
      if (job) reopenPicker(job, job.phase === "error" ? job.error : null);
    },

    dismiss: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      flushOrphan();
      const job = get().job;
      if (!job) return;
      reset();
      // A failed run sends the user back to the picker with the reason; a
      // clean one that was simply closed leaves the document as it was.
      if (job.phase === "error") {
        reopenPicker(job, job.error);
        return;
      }
      signalCompletion(job.mode, "closed");
    },

    pickDifferent: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      const job = get().job;
      if (!job) return;
      // Keep the draft: the user is re-validating against the same document.
      set({ job: null, minimized: false });
      reopenPicker(job, null);
    },

    markConsoleFinished: () => patch({ consoleFinished: true }),

    setStage: (stage) => {
      const job = get().job;
      if (!job || job.stage === stage) return;
      set({ job: { ...job, stage } });
    },

    minimize: () => set({ minimized: true }),
    restore: () => set({ minimized: false }),

    clear: () => {
      abortCtrl?.abort();
      abortCtrl = null;
      reset();
    },
  };
});

/* Safety net for the implicit exit: the user closes the tab mid-flow and the
   draft would otherwise be left behind. Every explicit path flushes directly.
   Route changes deliberately do NOT flush - running past them is the point. */
if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", () => {
    useProcessingJobStore.getState().flushOrphanDoc();
  });
}
